import {
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronsLeftRight,
  ChevronsRightLeft,
  ChevronUp,
  Network,
  Pencil,
  Plus,
  RefreshCw,
  Trash2,
  Users,
} from "lucide-react";
import { type CSSProperties, type ReactElement, useState } from "react";
import { ActionCell } from "@/components/shared/action-cell";
import { ConfirmDialog } from "@/components/shared/confirm-dialog";
import { EmptyState } from "@/components/shared/empty-state";
import { ErrorState } from "@/components/shared/error-state";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { SimpleTooltip } from "@/components/ui/tooltip";
import {
  type Department,
  type Pillar,
  type RoleTeam,
  useCreateRoleTeam,
  useDeleteRoleTeam,
  useOrganization,
  useReorderOrganization,
  useSyncOrgFromNos,
  useUpdateDepartment,
  useUpdatePillar,
  useUpdateRoleTeam,
} from "@/hooks/use-organization";
import { useSession } from "@/lib/auth-client";
import { canEditAll } from "@/lib/permissions";
import { cn } from "@/lib/utils";
import { OrgNodeDialog } from "./OrgNodeDialog";
import { RoleTeamDialog } from "./RoleTeamDialog";

type Level = "pillar" | "department" | "role_team";

type DialogState =
  | { type: "none" }
  | { type: "create"; level: Level; parentId?: string; defaultColor?: string }
  | { type: "edit"; level: Level; node: Pillar | Department | RoleTeam; defaultColor?: string }
  | { type: "delete"; level: Level; node: Pillar | Department | RoleTeam };

/**
 * Per-pillar accent themes. Cycled by pillar index so each column reads as a
 * distinct band — echoing the company org chart. Class strings are written
 * out in full (never template-constructed) so Tailwind can see them.
 */
interface PillarTheme {
  /** Stable palette key persisted on the pillar (`pillar.color`). */
  key: string;
  /** Representative hex for the picker's swatch dot. */
  swatch: string;
  band: string;
  bandText: string;
  cardBorder: string;
  deptBadge: string;
  guide: string;
}

/*
  Deliberately raw palette hues rather than the --success/--warning/--info
  tokens. These are CATEGORICAL, not semantic: one per pillar, cycled, and
  their only job is to stay tellable apart from each other. Mapping them to the
  status tokens would make green mean "good" where it only means "this pillar",
  and would collapse four distinct hues into a palette that carries meaning
  nobody intended. They are also deliberately independent of the workspace
  brand override, which can be any colour and would destroy the contrast
  between neighbouring pillars.
*/
const PILLAR_THEMES: PillarTheme[] = [
  {
    key: "violet",
    swatch: "#8b5cf6",
    band: "from-violet-500/15 to-violet-500/5 border-violet-300/60 dark:border-violet-500/30",
    bandText: "text-violet-700 dark:text-violet-300",
    cardBorder: "border-l-violet-400 dark:border-l-violet-500",
    deptBadge: "bg-violet-100 text-violet-700 dark:bg-violet-500/20 dark:text-violet-200",
    guide: "bg-violet-200 dark:bg-violet-500/40",
  },
  {
    key: "emerald",
    swatch: "#10b981",
    band: "from-emerald-500/15 to-emerald-500/5 border-emerald-300/60 dark:border-emerald-500/30",
    bandText: "text-emerald-700 dark:text-emerald-300",
    cardBorder: "border-l-emerald-400 dark:border-l-emerald-500",
    deptBadge: "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-200",
    guide: "bg-emerald-200 dark:bg-emerald-500/40",
  },
  {
    key: "sky",
    swatch: "#0ea5e9",
    band: "from-sky-500/15 to-sky-500/5 border-sky-300/60 dark:border-sky-500/30",
    bandText: "text-sky-700 dark:text-sky-300",
    cardBorder: "border-l-sky-400 dark:border-l-sky-500",
    deptBadge: "bg-sky-100 text-sky-700 dark:bg-sky-500/20 dark:text-sky-200",
    guide: "bg-sky-200 dark:bg-sky-500/40",
  },
  {
    key: "rose",
    swatch: "#f43f5e",
    band: "from-rose-500/15 to-rose-500/5 border-rose-300/60 dark:border-rose-500/30",
    bandText: "text-rose-700 dark:text-rose-300",
    cardBorder: "border-l-rose-400 dark:border-l-rose-500",
    deptBadge: "bg-rose-100 text-rose-700 dark:bg-rose-500/20 dark:text-rose-200",
    guide: "bg-rose-200 dark:bg-rose-500/40",
  },
  {
    key: "amber",
    swatch: "#f59e0b",
    band: "from-amber-500/15 to-amber-500/5 border-amber-300/60 dark:border-amber-500/30",
    bandText: "text-amber-700 dark:text-amber-300",
    cardBorder: "border-l-amber-400 dark:border-l-amber-500",
    deptBadge: "bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-200",
    guide: "bg-amber-200 dark:bg-amber-500/40",
  },
];

/**
 * The first theme, as the fallback everywhere one is needed. Declared here so
 * no call site needs a non-null assertion (banned by code-quality.md) to get
 * at `PILLAR_THEMES[0]` — the array is a module-level literal, but the type
 * system only knows that via this one narrowing.
 */
const DEFAULT_PILLAR_THEME: PillarTheme = PILLAR_THEMES[0] ?? {
  key: "violet",
  swatch: "#8b5cf6",
  band: "from-violet-500/15 to-violet-500/5 border-violet-300/60 dark:border-violet-500/30",
  bandText: "text-violet-700 dark:text-violet-300",
  cardBorder: "border-l-violet-400 dark:border-l-violet-500",
  deptBadge: "bg-violet-100 text-violet-700 dark:bg-violet-500/20 dark:text-violet-200",
  guide: "bg-violet-200 dark:bg-violet-500/40",
};

function themeFor(index: number): PillarTheme {
  return PILLAR_THEMES[index % PILLAR_THEMES.length] ?? DEFAULT_PILLAR_THEME;
}

/**
 * A single visual slot: either a curated Tailwind class (named/default themes,
 * which keep the tuned light/dark treatment) or an inline style (custom hex,
 * which Tailwind can't generate a class for).
 */
interface SlotStyle {
  className?: string;
  style?: CSSProperties;
}

/** The five accent slots a pillar theme drives across the tree. */
interface PillarVisual {
  band: SlotStyle;
  bandText: SlotStyle;
  cardBorder: SlotStyle;
  deptBadge: SlotStyle;
  guide: SlotStyle;
}

/** `#rrggbb` → `rgba(r, g, b, alpha)`; falls back to the raw input if unparsable. */
function hexToRgba(hex: string, alpha: number): string {
  const m = /^#([0-9a-fA-F]{2})([0-9a-fA-F]{2})([0-9a-fA-F]{2})$/.exec(hex);
  if (!m) return hex;
  const r = Number.parseInt(m[1]!, 16);
  const g = Number.parseInt(m[2]!, 16);
  const b = Number.parseInt(m[3]!, 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

/** Named/default theme → its curated class strings (unchanged appearance). */
function classVisual(t: PillarTheme): PillarVisual {
  return {
    band: { className: t.band },
    bandText: { className: t.bandText },
    cardBorder: { className: t.cardBorder },
    deptBadge: { className: t.deptBadge },
    guide: { className: t.guide },
  };
}

/** Custom hex → inline styles derived from the one color (flatter than curated). */
function hexVisual(hex: string): PillarVisual {
  return {
    band: {
      style: {
        backgroundImage: `linear-gradient(to bottom right, ${hexToRgba(hex, 0.15)}, ${hexToRgba(hex, 0.05)})`,
        borderColor: hexToRgba(hex, 0.4),
      },
    },
    bandText: { style: { color: hex } },
    cardBorder: { style: { borderLeftColor: hex } },
    deptBadge: { style: { backgroundColor: hexToRgba(hex, 0.18), color: hex } },
    guide: { style: { backgroundColor: hexToRgba(hex, 0.4) } },
  };
}

/**
 * Resolve a pillar's stored `color` to its visual slots. A "#hex" uses the
 * derived inline path; a named key uses that curated theme; null/unknown falls
 * back to the positional default — so pre-color-picker pillars are unchanged.
 */
function resolvePillarVisual(color: string | null | undefined, index: number): PillarVisual {
  if (color?.startsWith("#")) return hexVisual(color);
  const named = color ? PILLAR_THEMES.find((t) => t.key === color) : undefined;
  return classVisual(named ?? themeFor(index));
}

/** Preselected swatch/hex when opening the pillar dialog — stored color, else positional. */
function effectivePillarColor(color: string | null | undefined, index: number): string {
  return color ?? themeFor(index).key;
}

const PILLAR_SWATCHES = PILLAR_THEMES.map((t) => ({ key: t.key, swatch: t.swatch }));

/** Distinct people across every team in a department. */
function deptHeadcount(dept: Department): number {
  const ids = new Set<string>();
  for (const rt of dept.roleTeams) for (const p of rt.people) ids.add(p.id);
  return ids.size;
}

/** Distinct people across every team in a pillar. */
function pillarHeadcount(p: Pillar): number {
  const ids = new Set<string>();
  for (const d of p.departments)
    for (const rt of d.roleTeams) for (const x of rt.people) ids.add(x.id);
  return ids.size;
}

export function OrganizationTree() {
  const { data: session } = useSession();
  const isAdmin = canEditAll(session?.user?.role);
  const { data, isLoading, isError, error, refetch } = useOrganization();

  const [dialog, setDialog] = useState<DialogState>({ type: "none" });
  // The Team popup is its own state rather than a DialogState variant: it is
  // read-mostly (anyone can open it) while every DialogState case is an admin
  // create/edit/delete.
  //
  // Holds the team ID, not the object. Setting a lead invalidates the org query,
  // so a captured RoleTeam would keep rendering the previous lead while the tree
  // behind it showed the new one — deriving from `pillars` every render means the
  // dialog updates with the refetch.
  const [openTeamId, setOpenTeamId] = useState<string | null>(null);
  // Pillars can be collapsed to a narrow vertical strip so more fit on screen —
  // makes it obvious there are more pillars than the few that fit expanded.
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  const toggleCollapse = (id: string) =>
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const reorder = useReorderOrganization();
  const syncOrgFromNos = useSyncOrgFromNos();
  const updatePillar = useUpdatePillar();
  const updateDept = useUpdateDepartment();
  const createRT = useCreateRoleTeam();
  const updateRT = useUpdateRoleTeam();
  const deleteRT = useDeleteRoleTeam();

  function close() {
    setDialog({ type: "none" });
  }

  if (isLoading) {
    return (
      <div className="flex gap-4">
        <Skeleton className="h-96 w-full max-w-sm" />
        <Skeleton className="h-96 w-full max-w-sm" />
        <Skeleton className="h-96 w-full max-w-sm" />
      </div>
    );
  }
  if (isError) {
    return (
      <ErrorState
        message={error?.message ?? "Failed to load organization"}
        onRetry={() => refetch()}
      />
    );
  }
  const pillars = data?.pillars ?? [];
  if (pillars.length === 0) {
    return (
      <div className="space-y-4">
        <EmptyState
          icon={Network}
          title="No pillars yet"
          description={
            isAdmin
              ? "Pillars and departments come from NOS1 — sync to pull them in."
              : "Ask an admin to sync the organization tree from NOS1."
          }
        />
        {isAdmin && (
          <div className="flex justify-center">
            <Button onClick={() => syncOrgFromNos.mutate()} disabled={syncOrgFromNos.isPending}>
              <RefreshCw className="mr-2 h-4 w-4" />
              {syncOrgFromNos.isPending ? "Syncing…" : "Sync from NOS"}
            </Button>
          </div>
        )}
      </div>
    );
  }

  function moveSibling(
    level: Level,
    parentId: string | null,
    ids: string[],
    idx: number,
    dir: -1 | 1,
  ) {
    const target = idx + dir;
    if (target < 0 || target >= ids.length) return;
    const next = [...ids];
    const a = next[idx];
    const b = next[target];
    if (a === undefined || b === undefined) return;
    next[idx] = b;
    next[target] = a;
    reorder.mutate({ level, parentId, orderedIds: next });
  }

  const pillarIds = pillars.map((p) => p.id);
  // Re-derived every render so a lead change (which refetches the tree) is
  // reflected in the open dialog rather than in a stale captured object.
  const openTeam =
    openTeamId === null
      ? null
      : (pillars
          .flatMap((p) => p.departments)
          .flatMap((d) => d.roleTeams)
          .find((rt) => rt.id === openTeamId) ?? null);

  return (
    <div className="space-y-4">
      {/* Toolbar: pillar count (so it's clear the row scrolls) + a single
          Collapse-all / Expand-all toggle. */}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-muted-foreground text-sm">
          {pillars.length} pillar{pillars.length === 1 ? "" : "s"}
          <span className="text-muted-foreground/70"> · scroll sideways to see all</span>
        </p>
        <div className="flex items-center gap-2">
          {isAdmin && (
            <SimpleTooltip content="Pulls in any pillar or department NOS1 has that Enable doesn't yet. Names already here never change.">
              <Button
                variant="outline"
                size="sm"
                onClick={() => syncOrgFromNos.mutate()}
                disabled={syncOrgFromNos.isPending}
              >
                <RefreshCw className="mr-1.5 h-4 w-4" />
                {syncOrgFromNos.isPending ? "Syncing…" : "Sync from NOS"}
              </Button>
            </SimpleTooltip>
          )}
          {pillars.length > 1 && (
            <Button
              variant="outline"
              size="sm"
              // "Collapse all" until every pillar is collapsed, then flip to
              // "Expand all" — so collapsing a few by hand still leaves the button
              // able to collapse the rest.
              onClick={() =>
                setCollapsed(
                  collapsed.size === pillars.length ? new Set() : new Set(pillars.map((p) => p.id)),
                )
              }
            >
              {collapsed.size === pillars.length ? (
                <>
                  <ChevronsLeftRight className="mr-1.5 h-4 w-4" />
                  Expand all
                </>
              ) : (
                <>
                  <ChevronsRightLeft className="mr-1.5 h-4 w-4" />
                  Collapse all
                </>
              )}
            </Button>
          )}
        </div>
      </div>

      <div className="flex gap-4 overflow-x-auto pb-4">
        {pillars.map((pillar, pi) => {
          const visual = resolvePillarVisual(pillar.color, pi);
          const deptIds = pillar.departments.map((d) => d.id);
          // Collapsed: a narrow strip with the vertical pillar name + headcount.
          // Clicking anywhere on it expands the pillar again.
          if (collapsed.has(pillar.id)) {
            return (
              <button
                key={pillar.id}
                type="button"
                onClick={() => toggleCollapse(pillar.id)}
                title={`Expand ${pillar.name}`}
                // Fixed height so a collapsed strip is the same size whether some
                // or all pillars are collapsed (doesn't stretch to a sibling).
                className={cn(
                  "flex h-72 w-12 shrink-0 flex-col items-center justify-between rounded-xl border bg-gradient-to-b py-3 shadow-sm transition-[filter] hover:brightness-95",
                  visual.band.className,
                )}
                style={visual.band.style}
              >
                <ChevronRight
                  className={cn("h-4 w-4 shrink-0", visual.bandText.className)}
                  style={visual.bandText.style}
                />
                <span
                  className={cn(
                    "whitespace-nowrap font-display font-semibold text-sm [writing-mode:vertical-rl]",
                    visual.bandText.className,
                  )}
                  style={visual.bandText.style}
                >
                  {pillar.name}
                </span>
                {/* Headcount stays horizontal — a vertical icon+number rendered
                    as garbled artifacts. */}
                <span className="inline-flex shrink-0 items-center gap-1 text-muted-foreground text-xs">
                  <Users className="h-3 w-3" />
                  {pillarHeadcount(pillar)}
                </span>
              </button>
            );
          }
          return (
            <section
              key={pillar.id}
              className="flex w-[360px] shrink-0 flex-col overflow-hidden rounded-xl border bg-card shadow-sm"
            >
              {/* Pillar band */}
              <div
                className={cn(
                  "group/pillar border-b bg-gradient-to-br px-4 py-3",
                  visual.band.className,
                )}
                style={visual.band.style}
              >
                <div className="flex items-start gap-2">
                  <button
                    type="button"
                    onClick={() => toggleCollapse(pillar.id)}
                    title={`Collapse ${pillar.name}`}
                    className={cn(
                      "mt-0.5 shrink-0 rounded p-0.5 transition-colors hover:bg-black/10",
                      visual.bandText.className,
                    )}
                    style={visual.bandText.style}
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </button>
                  <div className="min-w-0 flex-1">
                    <h2
                      className={cn(
                        "break-words font-display font-semibold text-base",
                        visual.bandText.className,
                      )}
                      style={visual.bandText.style}
                    >
                      {pillar.name}
                    </h2>
                    <div className="mt-0.5 flex items-center gap-2 text-muted-foreground text-xs">
                      <span>
                        {pillar.departments.length} dept
                        {pillar.departments.length === 1 ? "" : "s"}
                      </span>
                      <span aria-hidden>·</span>
                      <span className="inline-flex items-center gap-1">
                        <Users className="h-3 w-3" />
                        {pillarHeadcount(pillar)}
                      </span>
                    </div>
                  </div>
                  {isAdmin && (
                    <NodeActions
                      reveal="pillar"
                      orientation="horizontal"
                      showPrev={pi > 0}
                      showNext={pi < pillars.length - 1}
                      onPrev={() => moveSibling("pillar", null, pillarIds, pi, -1)}
                      onNext={() => moveSibling("pillar", null, pillarIds, pi, 1)}
                      onEdit={() =>
                        setDialog({
                          type: "edit",
                          level: "pillar",
                          node: pillar,
                          defaultColor: effectivePillarColor(pillar.color, pi),
                        })
                      }
                    />
                  )}
                </div>
                {/* Description spans the full band width (below the header row) so
                    it isn't squeezed by the reserved NodeActions column. Indented
                    to line up under the pillar name. */}
                {pillar.description && (
                  <p className="mt-1 break-words pl-7 text-muted-foreground text-xs">
                    {pillar.description}
                  </p>
                )}
              </div>

              {/* Departments */}
              <div className="flex flex-1 flex-col gap-3 p-3">
                {pillar.departments.length === 0 && (
                  <p className="px-1 py-6 text-center text-muted-foreground text-xs">
                    No departments yet.
                  </p>
                )}
                {pillar.departments.map((dept, di) => (
                  <DepartmentBlock
                    key={dept.id}
                    dept={dept}
                    visual={visual}
                    isAdmin={isAdmin}
                    showPrev={di > 0}
                    showNext={di < pillar.departments.length - 1}
                    onPrev={() => moveSibling("department", pillar.id, deptIds, di, -1)}
                    onNext={() => moveSibling("department", pillar.id, deptIds, di, 1)}
                    onEdit={() => setDialog({ type: "edit", level: "department", node: dept })}
                    onAddTeam={() =>
                      setDialog({ type: "create", level: "role_team", parentId: dept.id })
                    }
                    onEditTeam={(rt) => setDialog({ type: "edit", level: "role_team", node: rt })}
                    onDeleteTeam={(rt) =>
                      setDialog({ type: "delete", level: "role_team", node: rt })
                    }
                    onMoveTeam={(rtIds, idx, dir) =>
                      moveSibling("role_team", dept.id, rtIds, idx, dir)
                    }
                    onOpenTeam={(rt) => setOpenTeamId(rt.id)}
                  />
                ))}
              </div>
            </section>
          );
        })}
      </div>

      <RoleTeamDialog team={openTeam} isAdmin={isAdmin} onClose={() => setOpenTeamId(null)} />

      {/* Only Role Team is ever opened here — Pillar and Department come from
          NOS1 (see "Sync from NOS" above) and have no create entry point. */}
      {dialog.type === "create" && (
        <OrgNodeDialog
          open
          onOpenChange={close}
          title={`Create ${labelFor(dialog.level)}`}
          description={`Adds a new ${labelFor(dialog.level).toLowerCase()} to the org tree.`}
          initial={{ name: "", description: null, parentId: dialog.parentId }}
          parent={parentPicker(dialog.level, pillars)}
          submitLabel="Create"
          onSubmit={async (values) => {
            const description = values.description?.trim() || null;
            await createRT.mutateAsync({
              departmentId: values.parentId as string,
              name: values.name,
              description,
            });
            close();
          }}
          isPending={createRT.isPending}
        />
      )}

      {dialog.type === "edit" && (
        <OrgNodeDialog
          open
          onOpenChange={close}
          title={`Edit ${labelFor(dialog.level)}`}
          initial={{
            name: dialog.node.name,
            description: dialog.node.description,
            parentId:
              dialog.level === "department"
                ? (dialog.node as Department).pillarId
                : dialog.level === "role_team"
                  ? (dialog.node as RoleTeam).departmentId
                  : undefined,
          }}
          parent={parentPicker(dialog.level, pillars)}
          locked={lockedFor(dialog.level)}
          color={
            dialog.level === "pillar"
              ? {
                  value:
                    (dialog.node as Pillar).color ??
                    dialog.defaultColor ??
                    DEFAULT_PILLAR_THEME.key,
                  swatches: PILLAR_SWATCHES,
                }
              : undefined
          }
          head={
            dialog.level === "pillar" ? { value: (dialog.node as Pillar).headPersonId } : undefined
          }
          submitLabel="Save"
          onSubmit={async (values) => {
            const description = values.description?.trim() || null;
            if (dialog.level === "pillar") {
              await updatePillar.mutateAsync({
                id: dialog.node.id,
                data: {
                  name: values.name,
                  description,
                  color: values.color ?? null,
                  headPersonId: values.headPersonId ?? null,
                },
              });
            } else if (dialog.level === "department") {
              await updateDept.mutateAsync({
                id: dialog.node.id,
                data: { pillarId: values.parentId, name: values.name, description },
              });
            } else {
              await updateRT.mutateAsync({
                id: dialog.node.id,
                data: { departmentId: values.parentId, name: values.name, description },
              });
            }
            close();
          }}
          isPending={updatePillar.isPending || updateDept.isPending || updateRT.isPending}
        />
      )}

      {/* Only Role Team is ever opened here — Pillar and Department come from
          NOS1 and have no delete entry point (see "Sync from NOS" above). */}
      {dialog.type === "delete" && (
        <ConfirmDialog
          open
          onOpenChange={close}
          title={`Delete ${labelFor(dialog.level).toLowerCase()}?`}
          description={deleteWarning(dialog)}
          confirmLabel="Delete"
          destructive
          onConfirm={() => {
            deleteRT.mutate({ id: dialog.node.id }, { onSuccess: () => close() });
          }}
        />
      )}
    </div>
  );
}

function parentPicker(level: Level, pillars: Pillar[]) {
  if (level === "department") {
    return {
      label: "Pillar",
      required: true,
      options: pillars.map((p) => ({ id: p.id, label: p.name })),
    };
  }
  if (level === "role_team") {
    return {
      label: "Department",
      required: true,
      options: pillars.flatMap((p) =>
        p.departments.map((d) => ({ id: d.id, label: `${p.name} / ${d.name}` })),
      ),
    };
  }
  return undefined;
}

/**
 * Pillar and Department always match NOS1, so their name — and a department's
 * parent pillar — can't be edited here. Role Team has no NOS counterpart and
 * is left fully editable.
 */
function lockedFor(
  level: Level,
): { name?: boolean; parent?: boolean; message: string } | undefined {
  if (level === "pillar") {
    return { name: true, message: "Synced from NOS1 — use Sync from NOS to pick up a rename." };
  }
  if (level === "department") {
    return {
      name: true,
      parent: true,
      message: "Synced from NOS1 — use Sync from NOS to pick up a rename.",
    };
  }
  return undefined;
}

/** Wrap a hover target in a description tooltip, or render it bare when empty. */
function MaybeDescriptionTooltip({
  description,
  children,
}: {
  description: string | null;
  children: ReactElement;
}) {
  if (!description) return children;
  return <SimpleTooltip content={description}>{children}</SimpleTooltip>;
}

function DepartmentBlock({
  dept,
  visual,
  isAdmin,
  showPrev,
  showNext,
  onPrev,
  onNext,
  onEdit,
  onAddTeam,
  onEditTeam,
  onDeleteTeam,
  onMoveTeam,
  onOpenTeam,
}: {
  dept: Department;
  visual: PillarVisual;
  isAdmin: boolean;
  showPrev: boolean;
  showNext: boolean;
  onPrev: () => void;
  onNext: () => void;
  onEdit: () => void;
  onAddTeam: () => void;
  onEditTeam: (rt: RoleTeam) => void;
  onDeleteTeam: (rt: RoleTeam) => void;
  onMoveTeam: (ids: string[], idx: number, dir: -1 | 1) => void;
  onOpenTeam: (rt: RoleTeam) => void;
}) {
  const headcount = deptHeadcount(dept);
  const rtIds = dept.roleTeams.map((rt) => rt.id);
  return (
    <div className="group/dept rounded-lg border bg-background/40">
      {/* Department header */}
      <div className="flex items-center gap-2.5 px-3 py-2.5">
        {/* Description (when present) surfaces on hover of the icon or name. */}
        <MaybeDescriptionTooltip description={dept.description}>
          <span
            className={cn(
              "flex size-8 shrink-0 items-center justify-center rounded-md font-semibold text-xs",
              visual.deptBadge.className,
            )}
            style={visual.deptBadge.style}
            aria-hidden
          >
            {abbreviate(dept.name)}
          </span>
        </MaybeDescriptionTooltip>
        <div className="min-w-0 flex-1">
          <MaybeDescriptionTooltip description={dept.description}>
            <div
              className={cn(
                "w-fit max-w-full break-words font-medium text-sm",
                dept.description && "cursor-help",
              )}
            >
              {dept.name}
            </div>
          </MaybeDescriptionTooltip>
          <div className="text-muted-foreground text-xs">
            {dept.roleTeams.length} team{dept.roleTeams.length === 1 ? "" : "s"}
            {headcount > 0 && ` · ${headcount} people`}
          </div>
        </div>
        {isAdmin && (
          <NodeActions
            reveal="dept"
            orientation="vertical"
            showPrev={showPrev}
            showNext={showNext}
            onPrev={onPrev}
            onNext={onNext}
            onEdit={onEdit}
          />
        )}
      </div>

      {/* Role teams */}
      {dept.roleTeams.length > 0 && (
        <div className="relative space-y-1.5 py-1 pr-2 pl-3">
          {/* Connector guide */}
          <div
            className={cn("absolute top-0 bottom-3 left-[18px] w-px", visual.guide.className)}
            style={visual.guide.style}
            aria-hidden
          />
          {dept.roleTeams.map((rt, ri) => (
            <RoleTeamCard
              key={rt.id}
              rt={rt}
              visual={visual}
              isAdmin={isAdmin}
              showPrev={ri > 0}
              showNext={ri < dept.roleTeams.length - 1}
              onPrev={() => onMoveTeam(rtIds, ri, -1)}
              onNext={() => onMoveTeam(rtIds, ri, 1)}
              onEdit={() => onEditTeam(rt)}
              onDelete={() => onDeleteTeam(rt)}
              onOpenTeam={onOpenTeam}
            />
          ))}
        </div>
      )}

      {isAdmin && (
        <div className="px-3 pt-0.5 pb-2 pl-[26px]">
          <Button
            variant="ghost"
            size="sm"
            className="h-7 text-muted-foreground text-xs"
            onClick={onAddTeam}
          >
            <Plus className="mr-1 h-3 w-3" />
            Add team
          </Button>
        </div>
      )}
    </div>
  );
}

function RoleTeamCard({
  rt,
  visual,
  isAdmin,
  showPrev,
  showNext,
  onPrev,
  onNext,
  onEdit,
  onDelete,
  onOpenTeam,
}: {
  rt: RoleTeam;
  visual: PillarVisual;
  isAdmin: boolean;
  showPrev: boolean;
  showNext: boolean;
  onPrev: () => void;
  onNext: () => void;
  onEdit: () => void;
  onDelete: () => void;
  onOpenTeam: (rt: RoleTeam) => void;
}) {
  return (
    <div
      className={cn(
        "group/team relative ml-3.5 flex items-center gap-2 rounded-md border border-l-[3px] bg-card px-2.5 py-1.5 shadow-xs transition-shadow hover:shadow-sm",
        "before:absolute before:top-1/2 before:-left-[7px] before:h-px before:w-[7px] before:bg-border",
        visual.cardBorder.className,
      )}
      style={visual.cardBorder.style}
    >
      <div className="min-w-0 flex-1">
        <div className="break-words font-medium text-sm">{rt.name}</div>
        {rt.description && (
          <div className="break-words text-muted-foreground text-xs">{rt.description}</div>
        )}
      </div>
      <button
        type="button"
        onClick={() => onOpenTeam(rt)}
        className="ml-3 flex shrink-0 items-center gap-1.5 rounded-md border bg-background px-2 py-1 text-muted-foreground text-xs transition-colors hover:bg-accent hover:text-foreground"
        aria-label={`View the ${rt.name} team`}
      >
        <Users className="size-3.5" />
        Team
      </button>
      {isAdmin && (
        // A persistent overflow menu, NOT the hover-revealed icon row the pillar
        // and department levels use. Those levels have no other control on the
        // row, but here ~100px of icons sliding in on hover shoved the Team
        // button leftwards mid-reach, making it hard to hit. One always-visible
        // 28px trigger means the Team button never moves, and it works on touch
        // and keyboard rather than depending on hover at all.
        <ActionCell
          className="size-7 shrink-0 text-muted-foreground"
          triggerLabel={`Actions for ${rt.name}`}
          items={[
            {
              icon: ChevronUp,
              label: "Move up",
              onClick: onPrev,
              disabled: !showPrev,
            },
            {
              icon: ChevronDown,
              label: "Move down",
              onClick: onNext,
              disabled: !showNext,
            },
            { icon: Pencil, label: "Edit team", onClick: onEdit },
            {
              icon: Trash2,
              label: "Delete team",
              onClick: onDelete,
              variant: "destructive",
              separatorBefore: true,
            },
          ]}
        />
      )}
    </div>
  );
}

/**
 * Reveal-on-hover scoping. Each level only un-hides its controls when its OWN
 * group is hovered — so hovering one team card doesn't light up every sibling.
 * Class strings are literal so Tailwind keeps them.
 */
const REVEAL_CLASS: Record<"pillar" | "dept" | "team", string> = {
  pillar: "group-hover/pillar:opacity-100",
  dept: "group-hover/dept:opacity-100",
  team: "group-hover/team:opacity-100",
};

/** Hover-revealed reorder/edit/delete controls shared by all three levels. */
function NodeActions({
  reveal,
  orientation,
  compact,
  showPrev,
  showNext,
  onPrev,
  onNext,
  onEdit,
  onDelete,
}: {
  reveal: "pillar" | "dept" | "team";
  orientation: "horizontal" | "vertical";
  compact?: boolean;
  showPrev: boolean;
  showNext: boolean;
  onPrev: () => void;
  onNext: () => void;
  onEdit: () => void;
  /** Omitted for Pillar/Department — synced from NOS1, so delete is unavailable here. */
  onDelete?: () => void;
}) {
  const Prev = orientation === "horizontal" ? ChevronLeft : ChevronUp;
  const Next = orientation === "horizontal" ? ChevronRight : ChevronDown;
  const size = compact ? "size-6" : "size-7";
  const icon = compact ? "h-3 w-3" : "h-3.5 w-3.5";
  return (
    <div
      className={cn(
        "flex shrink-0 items-center gap-0.5 opacity-0 transition-opacity focus-within:opacity-100",
        REVEAL_CLASS[reveal],
      )}
    >
      <button
        type="button"
        disabled={!showPrev}
        onClick={onPrev}
        title="Move earlier"
        className={cn(
          size,
          "flex items-center justify-center rounded text-muted-foreground hover:bg-accent hover:text-foreground disabled:opacity-30",
        )}
      >
        <Prev className={icon} />
      </button>
      <button
        type="button"
        disabled={!showNext}
        onClick={onNext}
        title="Move later"
        className={cn(
          size,
          "flex items-center justify-center rounded text-muted-foreground hover:bg-accent hover:text-foreground disabled:opacity-30",
        )}
      >
        <Next className={icon} />
      </button>
      <button
        type="button"
        onClick={onEdit}
        title="Edit"
        className={cn(
          size,
          "flex items-center justify-center rounded text-muted-foreground hover:bg-accent hover:text-foreground",
        )}
      >
        <Pencil className={icon} />
      </button>
      {onDelete && (
        <button
          type="button"
          onClick={onDelete}
          title="Delete"
          className={cn(
            size,
            "flex items-center justify-center rounded text-muted-foreground hover:bg-destructive/10 hover:text-destructive",
          )}
        >
          <Trash2 className={icon} />
        </button>
      )}
    </div>
  );
}

function labelFor(level: Level): string {
  return level === "pillar" ? "Pillar" : level === "department" ? "Department" : "Role Team";
}

/** Up to two uppercase initials from a multi-word name, else first 2 chars. */
function abbreviate(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    return ((parts[0]?.[0] ?? "") + (parts[1]?.[0] ?? "")).toUpperCase();
  }
  return name.slice(0, 2).toUpperCase();
}

function deleteWarning(d: Extract<DialogState, { type: "delete" }>): string {
  if (d.level === "pillar") {
    const p = d.node as Pillar;
    const deptCount = p.departments.length;
    const rtCount = p.departments.reduce((n, x) => n + x.roleTeams.length, 0);
    if (deptCount === 0) return `Permanently deletes ${p.name}. This cannot be undone.`;
    return `Permanently deletes ${p.name} AND its ${deptCount} department${deptCount === 1 ? "" : "s"}${rtCount > 0 ? ` and ${rtCount} role team${rtCount === 1 ? "" : "s"}` : ""}. This cannot be undone.`;
  }
  if (d.level === "department") {
    const dept = d.node as Department;
    const rtCount = dept.roleTeams.length;
    if (rtCount === 0) return `Permanently deletes ${dept.name}. This cannot be undone.`;
    return `Permanently deletes ${dept.name} AND its ${rtCount} role team${rtCount === 1 ? "" : "s"}. This cannot be undone.`;
  }
  const rt = d.node as RoleTeam;
  const n = rt.people.length;
  if (n === 0) return `Permanently deletes ${rt.name}. This cannot be undone.`;
  return `Permanently deletes ${rt.name}. ${n} ${n === 1 ? "person is" : "people are"} placed on this team and will be un-assigned from it (their profiles are not deleted). This cannot be undone.`;
}
